import type {I18n} from "@lingui/core";
import {msg} from "@lingui/core/macro";
import {createSorter} from "../utils/sort-utils";
import createId, {IdType} from "./create-id";
import type {CategoryEntry, CategoryGroupId, GroupRepo, ItemEntry, UnitEntry} from "./GroupRepo";

// these ids are meant to be picked up by the lingui extractor
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const messages = [
  msg({id: "cat.fruit-vegetables"}),
  msg({id: "cat.bread"}),
  msg({id: "cat.dairy"}),
  msg({id: "cat.spices"}),
  msg({id: "cat.meat"}),
  msg({id: "cat.convenience"}),
  msg({id: "cat.grain"}),
  msg({id: "cat.snacks"}),
  msg({id: "cat.drinks"}),
  msg({id: "cat.household"}),
  msg({id: "cat.care"}),
  msg({id: "cat.diy"}),
  msg({id: "cat.pet"}),

  msg({id: "item.apple"}),
  msg({id: "item.carrot"}),
  msg({id: "item.potato"}),
  msg({id: "item.cucumber"}),
  msg({id: "item.bell-pepper"}),
  msg({id: "item.whole-wheat-bread"}),
  msg({id: "item.baguette"}),
  msg({id: "item.rye-bread"}),
  msg({id: "item.toast"}),
  msg({id: "item.pretzel"}),
  msg({id: "item.milk"}),
  msg({id: "item.butter"}),
  msg({id: "item.cheese"}),
  msg({id: "item.yogurt"}),
  msg({id: "item.cream"}),
  msg({id: "item.salt"}),
  msg({id: "item.pepper"}),
  msg({id: "item.paprika"}),
  msg({id: "item.cinnamon"}),
  msg({id: "item.thyme"}),
  msg({id: "item.chicken-breast"}),
  msg({id: "item.pork-chops"}),
  msg({id: "item.beef-steak"}),
  msg({id: "item.sausages"}),
  msg({id: "item.salmon-fillet"}),
  msg({id: "item.pasta"}),
  msg({id: "item.pizza"}),
  msg({id: "item.canned-soup"}),
  msg({id: "item.frozen-vegetables"}),
  msg({id: "item.ready-made-sauce"}),
  msg({id: "item.rice"}),
  msg({id: "item.quinoa"}),
  msg({id: "item.oats"}),
  msg({id: "item.barley"}),
  msg({id: "item.couscous"}),
  msg({id: "item.chocolate"}),
  msg({id: "item.chips"}),
  msg({id: "item.nuts"}),
  msg({id: "item.dried-fruits"}),
  msg({id: "item.popcorn"}),
  msg({id: "item.water"}),
  msg({id: "item.orange-juice"}),
  msg({id: "item.cola"}),
  msg({id: "item.beer"}),
  msg({id: "item.coffee"}),
  msg({id: "item.dish-soap"}),
  msg({id: "item.laundry-detergent"}),
  msg({id: "item.toilet-paper"}),
  msg({id: "item.trash-bags"}),
  msg({id: "item.cleaning-spray"}),
  msg({id: "item.shampoo"}),
  msg({id: "item.toothpaste"}),
  msg({id: "item.body-lotion"}),
  msg({id: "item.razor"}),
  msg({id: "item.deodorant"}),
  msg({id: "item.screwdriver"}),
  msg({id: "item.paintbrush"}),
  msg({id: "item.nails"}),
  msg({id: "item.hammer"}),
  msg({id: "item.sandpaper"}),
  msg({id: "item.cat-food"}),
  msg({id: "item.dog-treats"}),
  msg({id: "item.cat-litter"}),
  msg({id: "item.bird-seeds"}),
  msg({id: "item.fish-food"}),

  msg({id: "units.piece"}),
  msg({id: "unit.kg"}),
  msg({id: "unit.g"}),
  msg({id: "unit.ml"}),
  msg({id: "unit.l"}),
];

const initialCategories = [
  {name: "fruit-vegetables", emoji: "🍆"},
  {name: "bread", emoji: "🍞"},
  {name: "dairy", emoji: "🥛"},
  {name: "spices", emoji: "🌶️"},
  {name: "meat", emoji: "🥩"},
  {name: "convenience", emoji: "🧑‍🍳"},
  {name: "grain", emoji: "🌾"},
  {name: "snacks", emoji: "🍪"},
  {name: "drinks", emoji: "🥤"},
  {name: "household", emoji: "🫧"},
  {name: "care", emoji: "🧴"},
  {name: "diy", emoji: "🔨"},
  {name: "pet", emoji: "🐱"},
];

const initialItems = [
  {name: "apple", category: "fruit-vegetables", icon: "🍎"},
  {name: "carrot", category: "fruit-vegetables", icon: "🥕"},
  {name: "potato", category: "fruit-vegetables", icon: "🥔"},
  {name: "cucumber", category: "fruit-vegetables", icon: "🥒"},
  {name: "bell-pepper", category: "fruit-vegetables", icon: "🫑"},

  {name: "whole-wheat-bread", category: "bread", icon: "🍞"},
  {name: "baguette", category: "bread", icon: "🥖"},
  {name: "rye-bread", category: "bread", icon: "🌾"},
  {name: "toast", category: "bread", icon: "🍞"},
  {name: "pretzel", category: "bread", icon: "🥨"},

  {name: "milk", category: "dairy", icon: "🥛"},
  {name: "butter", category: "dairy", icon: "🧈"},
  {name: "cheese", category: "dairy", icon: "🧀"},
  {name: "yogurt", category: "dairy", icon: "⚪️"},
  {name: "cream", category: "dairy", icon: "🧁"},

  {name: "salt", category: "spices", icon: "🧂"},
  {name: "pepper", category: "spices", icon: "⚫️"},
  {name: "paprika", category: "spices", icon: "🔴"},
  {name: "cinnamon", category: "spices", icon: "🍂"},
  {name: "thyme", category: "spices", icon: "🌿"},

  {name: "chicken-breast", category: "meat", icon: "🐓"},
  {name: "pork-chops", category: "meat", icon: "🐷"},
  {name: "beef-steak", category: "meat", icon: "🥩"},
  {name: "sausages", category: "meat", icon: "🌭"},
  {name: "salmon-fillet", category: "meat", icon: "🍣"},

  {name: "pasta", category: "convenience", icon: "🍝"},
  {name: "pizza", category: "convenience", icon: "🍕"},
  {name: "canned-soup", category: "convenience", icon: "🍯"},
  {name: "frozen-vegetables", category: "convenience", icon: "🥦"},
  {name: "ready-made-sauce", category: "convenience", icon: "🥫"},

  {name: "rice", category: "grain", icon: "🍚"},
  {name: "quinoa", category: "grain", icon: "🌾"},
  {name: "oats", category: "grain", icon: "🌾"},
  {name: "barley", category: "grain", icon: "🌾"},
  {name: "couscous", category: "grain", icon: "🌾"},

  {name: "chocolate", category: "snacks", icon: "🍫"},
  {name: "chips", category: "snacks", icon: "🟡"},
  {name: "nuts", category: "snacks", icon: "🥜"},
  {name: "dried-fruits", category: "snacks", icon: "🍇"},
  {name: "popcorn", category: "snacks", icon: "🍿"},

  {name: "water", category: "drinks", icon: "🚰"},
  {name: "orange-juice", category: "drinks", icon: "🧃"},
  {name: "cola", category: "drinks", icon: "🥤"},
  {name: "beer", category: "drinks", icon: "🍺"},
  {name: "coffee", category: "drinks", icon: "☕️"},

  {name: "dish-soap", category: "household", icon: "🧼"},
  {name: "laundry-detergent", category: "household", icon: "🧹"},
  {name: "toilet-paper", category: "household", icon: "🧻"},
  {name: "trash-bags", category: "household", icon: "🗑️"},
  {name: "cleaning-spray", category: "household", icon: "🚽"},

  {name: "shampoo", category: "care", icon: "🧼"},
  {name: "toothpaste", category: "care", icon: "🪥"},
  {name: "body-lotion", category: "care", icon: "🧼"},
  {name: "razor", category: "care", icon: "🗡️"},
  {name: "deodorant", category: "care", icon: "📢"},

  {name: "screwdriver", category: "diy", icon: "🪛"},
  {name: "paintbrush", category: "diy", icon: "👩‍🎨"},
  {name: "nails", category: "diy", icon: "🔩"},
  {name: "hammer", category: "diy", icon: "🔨"},
  {name: "sandpaper", category: "diy", icon: "📜"},

  {name: "cat-food", category: "pet", icon: "🐱"},
  {name: "dog-treats", category: "pet", icon: "🐕"},
  {name: "cat-litter", category: "pet", icon: "🐱"},
  {name: "bird-seeds", category: "pet", icon: "🐦"},
  {name: "fish-food", category: "pet", icon: "🐟"},
] satisfies {name: string; category: string; icon: string | null}[];

const initialUnits = ["kg", "g", "ml", "l"];

export const fillInitialItems = (repo: GroupRepo, i18n: I18n, catGroupId: CategoryGroupId) => {
  const sortIdxs = createSorter()
    .getMudder()
    .mudder("", "", initialCategories.length, undefined, initialCategories.length + 10);
  const now = new Date();
  let i = 0;
  const catByName = new Map<string, CategoryEntry>();
  for (const c of initialCategories) {
    const id = createId(IdType.Category);
    const cat: CategoryEntry = {
      id,
      name: i18n.t({id: `cat.${c.name}`}),
      emoji: c.emoji,
      createdAt: now,
    };
    catByName.set(c.name, cat);
    repo.Categories.set(id, cat);
    const catGroupEntryId = createId(IdType.CategoryGroupEntry);
    repo.CategoryGroupEntries.set(catGroupEntryId, {
      id: catGroupEntryId,
      categoryId: id,
      categoryGroupId: catGroupId,
      sortIndex: sortIdxs[i],
    });
    i += 1;
  }

  for (const i of initialItems) {
    const item: ItemEntry = {
      id: createId(IdType.Item),
      name: i18n.t({id: `item.${i.name}`}),
      categoryId: catByName.get(i.category)!.id,
      createdAt: now,
      emoji: i.icon,
    };
    repo.Items.set(item.id, item);
  }
};

export const fillInitialUnits = (repo: GroupRepo, i18n: I18n) => {
  const sortIdxs = createSorter()
    .getMudder()
    .mudder("", "", initialUnits.length, undefined, initialCategories.length + 25);
  let i = 0;
  for (const unit of initialUnits) {
    const unitId = createId(IdType.Unit);
    const unitEntry: UnitEntry = {
      id: unitId,
      sortIndex: sortIdxs[i],
      name: i18n.t({id: `unit.${unit}`}),
      createdAt: new Date(),
    };
    repo.Units.set(unitId, unitEntry);
    i += 1;
  }
};
